import { EventEmitter } from 'events'
import { DeviceManifest, usageType } from '../types/manifest'
import { BaseDevice } from '../runtime/BaseDevice'
import { DeviceFactory } from '../runtime/DeviceFactory'
import { HidChannelFactory } from '../channels/hid/HidChannelFactory'
import { WebHidChannel } from '../channels/hid/webhid/WebHidChannel' // 用于扫描
import { NodeHidChannel } from '../channels/hid/nodehid/NodeHidChannel'
import type { HidDeviceFilter } from '../channels/hid/HidChannelDescriptor'
import type { ChannelDescriptor } from '../channels/Channel'
import type { NormalizedDeviceManifest } from '../manifests/ManifestTypes'
import { normalizeManifests } from '../manifests/ManifestNormalizer'
import { validateManifestHardware } from '../manifests/ManifestValidator'
import { ManagerEvents } from '../events/ManagerEvents'
import { DEFAULT_FIRMWARE_VERSION_POLICY, type FirmwareVersionPolicy } from '../types/firmware'
import { ConnectionType } from '../types/connection'

/**
 * Rapoo 设备管理器 (God Object)
 * 负责：扫描、连接、生命周期管理
 */

/**
 * RapooDeviceManager 初始化选项。
 * 用于把"应用层关心的运行时配置"显式注入到 SDK，避免在 chips 中引入应用层概念。
 */
export interface PairingDeviceIdentity {
  vendorId: number
  productId: number
}

export type PairingContextStatus = 'pairing' | 'completed'

export interface PairingContext {
  kind: 'receiver' | 'base'
  /** completed 表示配对已结束，但页面尚未退出、context 仍需保留。 */
  status: PairingContextStatus
  source: PairingDeviceIdentity
  target: PairingDeviceIdentity
}

export interface RapooDeviceManagerOptions {
  /**
   * 固件升级文件 base URL。
   *
   * 由应用层根据运行时渠道（国内 / 海外）选择后传入；缺省时回落至国内默认地址。
   *
   * 注意：仅在 SDK 内部使用，应用层应通过 {@link RapooDeviceManager.getFirmwareBaseUrl} 读取。
   */
  firmwareBaseUrl?: string

  /**
   * 固件版本资格策略。`any-version` 允许同版本重刷和降级，
   * 但不会绕过设备特有的协议安全门。
   */
  firmwareVersionPolicy?: FirmwareVersionPolicy
}

/** SDK 内置的默认固件 base URL（国内）。海外渠道由应用层注入。 */
export const DEFAULT_FIRMWARE_BASE_URL = 'https://soft.rapoo.com/RapooDriver/Firmware'

/** 可自动建连的主通道：日常 APP + 独立 VID/PID 的 Boot update */
const AUTO_CONNECT_USAGE_TYPES: ReadonlySet<usageType> = new Set(['default', 'update'])

/**
 * WebHID 复合设备重枚举会连续触发多个 connect/disconnect 事件。
 * 等事件短暂稳定后再读取完整 HID 列表，避免用首个不完整 HIDDevice 创建长期实例。
 */
const WEB_HID_HOTPLUG_SETTLE_MS = 500

export class RapooDeviceManager extends EventEmitter {
  private static instance: RapooDeviceManager
  private manifests: NormalizedDeviceManifest[]
  private devices: Map<any, BaseDevice> = new Map()
  private firmwareBaseUrl: string = DEFAULT_FIRMWARE_BASE_URL
  private firmwareVersionPolicy: FirmwareVersionPolicy = DEFAULT_FIRMWARE_VERSION_POLICY
  /**
   * 共享同一 Boot VID/PID 的多型号消歧：
   * key=`vendorId:productId` → 最近一次在线的 APP `manifest.model`。
   * 内存 + sessionStorage：刷新后仍可认回；皆无则发 BootAmbiguous 由用户选择。
   */
  private bootOwnerModelByKey = new Map<string, string>()
  /** 等待用户选定型号后再建连的 Boot 原始 HID 设备 */
  private pendingAmbiguousBoots = new Map<string, unknown>()
  /**
   * 有线 USB 鼠标刚拔出时置位：若当时底座也在重枚举，等底座 default 通道连上后再补一次
   * emitCurrentConnectedMouse（一次性，不是轮询）。
   */
  private pendingDockChildRefreshAfterWiredMouseLeave = false
  private pairingContext: PairingContext | null = null
  private webHidRestoreTimer: ReturnType<typeof setTimeout> | null = null
  private webHidLifecycle: Promise<void> = Promise.resolve()

  /** 等待拔线时同步唯一目标；开始配对后保留，退出步骤时传 null。 */
  public setPairingContext(context: PairingContext | null): void {
    this.pairingContext = context
      ? { ...context, source: { ...context.source }, target: { ...context.target } }
      : null
  }

  public getPairingContext(): PairingContext | null {
    return this.pairingContext
  }

  /** 只更新仍然存在的 context，避免异步配对结束晚于组件卸载时重新创建 context。 */
  public setPairingContextStatus(status: PairingContextStatus): void {
    if (!this.pairingContext) return
    this.pairingContext = { ...this.pairingContext, status }
  }

  private samePairingIdentity(a: PairingDeviceIdentity | null, b: PairingDeviceIdentity): boolean {
    return a?.vendorId === b.vendorId && a.productId === b.productId
  }

  private getPairingIdentity(device: BaseDevice): PairingDeviceIdentity {
    return {
      vendorId: device.manifest.hardware.vendorId,
      productId: device.manifest.hardware.currentPid,
    }
  }

  public isPairingDevice(device: BaseDevice): boolean {
    const context = this.pairingContext
    if (!context) return false
    const identity = this.getPairingIdentity(device)
    return (
      this.samePairingIdentity(identity, context.source) ||
      this.samePairingIdentity(identity, context.target)
    )
  }

  /**
   * 获取 RapooDeviceManager 单例实例
   * @param manifests 设备清单列表 (首次调用必须传入)
   * @param options 可选的运行时配置；仅首次调用生效
   */
  public static getInstance(
    manifests?: DeviceManifest[],
    options?: RapooDeviceManagerOptions,
  ): RapooDeviceManager {
    if (!RapooDeviceManager.instance) {
      if (!manifests) {
        throw new Error('[RapooDeviceManager] First call to getInstance must provide manifests.')
      }
      RapooDeviceManager.instance = new RapooDeviceManager(manifests, options)
    }
    return RapooDeviceManager.instance
  }

  private constructor(manifests: DeviceManifest[], options?: RapooDeviceManagerOptions) {
    super()
    if (options?.firmwareBaseUrl) {
      this.firmwareBaseUrl = options.firmwareBaseUrl
    }
    if (options?.firmwareVersionPolicy === 'any-version') {
      this.firmwareVersionPolicy = options.firmwareVersionPolicy
    }
    const manifestErrors = manifests.flatMap(validateManifestHardware)
    if (manifestErrors.length > 0) {
      console.warn('[RapooDeviceManager] Manifest validation warnings:', manifestErrors)
    }
    this.manifests = normalizeManifests(manifests)
    this.setupHotPlugListeners()
  }

  /**
   * 获取固件升级文件 base URL（由应用层在初始化时注入）。
   * chips / feature 通过 `device.getFirmwareBaseUrl()` 间接访问。
   */
  public getFirmwareBaseUrl(): string {
    return this.firmwareBaseUrl
  }

  /** 获取当前 SDK 实例的固件版本资格策略。 */
  public getFirmwareVersionPolicy(): FirmwareVersionPolicy {
    return this.firmwareVersionPolicy
  }

  /**
   * 恢复已连接的设备 (用于页面刷新后自动重连)
   */
  async restore(): Promise<void> {
    console.log('[RapooDeviceManager] Restoring devices...')

    // 目前仅支持 WebHID 的恢复
    if (WebHidChannel.isSupported()) {
      try {
        const devices = await WebHidChannel.getConnected()
        console.log('[RapooDeviceManager] Found connected devices:', devices)

        for (const device of devices) {
          // 这里不做严格的过滤，因为 handleDeviceConnected 内部会检查 Manifest
          // 只要是已授权的 HID 设备，都尝试连接
          await this.handleDeviceConnected(device)
        }
      } catch (error) {
        console.error('[RapooDeviceManager] Restore failed:', error)
      }
    }
  }

  /**
   * 启动设备扫描
   */
  async scan(): Promise<void> {
    console.log('[RapooDeviceManager] Starting scan...')

    // 1. 按通道类型对 Manifest 进行分组
    const hidManifests = this.manifests.filter((m) => m.hardware.transport === 'HID')
    // const usbManifests = this.manifests.filter(m => m.hardware.transport === 'USB'); // 预留
    // const bleManifests = this.manifests.filter(m => m.hardware.transport === 'BLE'); // 预留

    // 2. 并行执行各协议的扫描
    const scanPromises: Promise<any>[] = []

    // --- HID 扫描 ---
    if (hidManifests.length > 0) {
      const hidFilters = hidManifests.flatMap((m) => {
        const appPids = Array.isArray(m.hardware.productId)
          ? m.hardware.productId
          : [m.hardware.productId]

        // 使用归一化后的 channels 生成扫描过滤器；channel 可覆盖独立 Boot VID/PID。
        const hidChannels = m.channels.filter((channel) => channel.kind === 'hid')
        if (hidChannels.length > 0) {
          return hidChannels.flatMap((channel) => {
            const vendorId = channel.vendorId ?? m.hardware.vendorId
            const productIds = channel.productId
              ? Array.isArray(channel.productId)
                ? channel.productId
                : [channel.productId]
              : appPids
            return productIds.map((productId) => ({
              vendorId,
              productId,
              usagePage: channel.usagePage,
              usage: channel.usage,
            }))
          })
        }

        // 未声明 HID interface 时，只能退化为 VID/PID 扫描；新设备应尽量补全 usagePage/usage。
        return appPids.map((pid) => ({
          vendorId: m.hardware.vendorId,
          productId: pid,
        }))
      })

      // 策略：如果支持 NodeHID (Electron/Node)，优先使用它 (无弹窗)
      // 否则使用 WebHID (浏览器，可能需要弹窗)
      if (NodeHidChannel.isSupported()) {
        console.log('[RapooDeviceManager] Using NodeHID channel')
        scanPromises.push(
          NodeHidChannel.scan(hidFilters).then((devices) => {
            return devices.map((d) => ({ raw: d, channelKind: 'HID' }))
          }),
        )
      } else if (WebHidChannel.isSupported()) {
        console.log('[RapooDeviceManager] Using WebHID channel')
        // 添加 HID 扫描任务
        scanPromises.push(
          WebHidChannel.scan(this.toWebHIDRequestFilters(hidFilters)).then((devices) => {
            return devices.map((d) => ({ raw: d, channelKind: 'HID' }))
          }),
        )
      } else {
        console.warn('[RapooDeviceManager] No supported HID channel found')
      }
    }

    // --- USB/BLE 扫描 (未来扩展) ---
    // if (usbManifests.length > 0) { ... }

    // 3. 等待所有扫描完成
    const results = await Promise.all(scanPromises)
    const allDevices = results.flat() // 拍平结果数组

    // 4. 处理所有发现的设备
    for (const item of allDevices) {
      // handleDeviceConnected 会基于 VID/PID 与 HID interface 再做一次校验。
      await this.handleDeviceConnected(item.raw)
    }
  }

  /**
   * 获取指定类型的设备 (提供完美的类型提示)
   * @param model 可选，按型号筛选
   */
  getDevice<T extends BaseDevice>(model?: string): T | undefined {
    for (const device of this.devices.values()) {
      if (!model || device.manifest.model === model) {
        return device as T
      }
    }
    return undefined
  }

  /**
   * 获取所有设备 (提供完美的类型提示)
   * @param model 可选，按型号筛选
   */
  getDevices<T extends BaseDevice>(model?: string): T[] {
    return Array.from(this.devices.values()).filter((device) => {
      return !model || device.manifest.model === model
    }) as T[]
  }

  /**
   * 内部：处理设备连接
   */
  private async handleDeviceConnected(rawDevice: any): Promise<void> {
    // 1. 基础校验：必须包含 VID/PID
    if (
      !rawDevice ||
      typeof rawDevice.vendorId !== 'number' ||
      typeof rawDevice.productId !== 'number'
    ) {
      return
    }

    // 2. 匹配 Manifest（含独立 Boot update 身份；多型号无归属时进入消歧）
    const resolved = this.resolveManifestForDevice(rawDevice.vendorId, rawDevice.productId)
    if (resolved.kind === 'ambiguous') {
      const key = this.bootIdentityKey(rawDevice.vendorId, rawDevice.productId)
      this.pendingAmbiguousBoots.set(key, rawDevice)
      console.warn(
        `[RapooDeviceManager] Boot VID/PID 对应多个型号且无归属记录，等待用户选择: ${resolved.models.join(', ')}`,
      )
      this.emit(ManagerEvents.BootAmbiguous, {
        vendorId: rawDevice.vendorId,
        productId: rawDevice.productId,
        models: resolved.models,
      })
      return
    }
    if (resolved.kind !== 'ok') return
    const manifest = resolved.manifest
    // 更新实际使用的 PID
    manifest.hardware.currentPid = rawDevice.productId

    // 3. 接口匹配校验 (关键修复：防止连接到错误的 Interface)
    const matchResult = this.isDeviceMatchFilter(rawDevice, manifest)
    if (!matchResult.matched) return

    const usage = matchResult.usageType ?? 'default'
    // 日常 APP + 独立 VID/PID 的 Boot update 可自动建连；updateWCH/pairing 仍按需打开。
    if (!AUTO_CONNECT_USAGE_TYPES.has(usage)) return
    // update 仅当 filter 声明了独立 VID/PID 时才作为主设备建连（避免同机 sibling 误标）
    if (usage === 'update' && !this.hasIndependentUpdateIdentity(manifest, rawDevice)) {
      return
    }

    // 4. 查重
    if (this.devices.has(rawDevice)) return

    try {
      // 5. 创建 HID 通道
      const channel = HidChannelFactory.create(manifest.hardware.transport, rawDevice)

      // 6. 创建 Device (驱动)
      const device = DeviceFactory.create(manifest, channel)
      device.primaryUsageType = usage

      // 6.1 把固件 base URL 注入设备实例（chips 通过 device.getFirmwareBaseUrl() 读取）
      device.__setFirmwareBaseUrl(this.firmwareBaseUrl)
      device.__setFirmwareVersionPolicy(this.firmwareVersionPolicy)

      if (usage === 'default') {
        this.rememberBootOwnerFromAppManifest(manifest)
      }

      // 7. 存储实例
      this.devices.set(rawDevice, device)

      // 8. 自动打开连接
      await device.open()

      console.log(
        `[RapooDeviceManager] Device Connected: ${manifest.model}` +
          (usage === 'update' ? ' (firmware update mode)' : ''),
      )
      this.emit(ManagerEvents.DeviceConnected, device)
      this.maybeRefreshDockChildAfterWiredMouseLeave(device)
    } catch (error) {
      console.error(`[RapooDeviceManager] Device Init Failed (${manifest.model}):`, error)
      // 初始化失败时清理 map，避免占位
      this.devices.delete(rawDevice)
    }
  }

  /**
   * 校验设备接口是否符合 Manifest 定义的过滤器
   *
   * @param device 原始设备对象 (WebHID 或 NodeHID)
   * @param manifest 设备清单
   * @returns matched: 是否匹配，filterType: 匹配的过滤器类型
   */
  private isDeviceMatchFilter(
    device: any,
    manifest: NormalizedDeviceManifest,
  ): { matched: boolean; usageType?: usageType } {
    // 未定义 HID interface 时，只能退化为 VID/PID 匹配。
    if (!manifest.hardware.filters || manifest.hardware.filters.length === 0) {
      return { matched: true, usageType: 'default' }
    }

    const deviceUsages = this.getDeviceUsages(device)
    const vid = device.vendorId as number
    const pid = device.productId as number

    for (const channel of manifest.channels) {
      if (!this.channelIdentityMatches(channel, vid, pid, manifest)) continue

      if (channel.usagePage === undefined || channel.usage === undefined) {
        return { matched: true, usageType: channel.usageType }
      }

      // 未 open 时 collections 可能为空：独立 VID 的 update 通道允许仅按身份命中
      if (!deviceUsages.length) {
        if (channel.vendorId !== undefined && channel.productId !== undefined) {
          return { matched: true, usageType: channel.usageType }
        }
        continue
      }

      if (
        deviceUsages.some((u) => u.usagePage === channel.usagePage && u.usage === channel.usage)
      ) {
        return { matched: true, usageType: channel.usageType }
      }
    }

    return { matched: false }
  }

  /** channel 是否覆盖独立 VID/PID；未覆盖则必须等于 APP 身份 */
  private channelIdentityMatches(
    channel: ChannelDescriptor,
    vid: number,
    pid: number,
    manifest: NormalizedDeviceManifest,
  ): boolean {
    const expectedVid = channel.vendorId ?? manifest.hardware.vendorId
    const expectedPids = channel.productId
      ? Array.isArray(channel.productId)
        ? channel.productId
        : [channel.productId]
      : Array.isArray(manifest.hardware.productId)
        ? manifest.hardware.productId
        : [manifest.hardware.productId]
    return expectedVid === vid && expectedPids.includes(pid)
  }

  /**
   * update 自动建连仅针对声明了独立 Boot VID/PID 的通道
   *（与 APP 同 VID 的 sibling update 仍走 session.openDevice）。
   */
  private hasIndependentUpdateIdentity(
    manifest: NormalizedDeviceManifest,
    rawDevice: { vendorId: number; productId: number },
  ): boolean {
    return manifest.channels.some((channel) => {
      if (channel.usageType !== 'update') return false
      if (channel.vendorId === undefined || channel.productId === undefined) return false
      return this.channelIdentityMatches(channel, rawDevice.vendorId, rawDevice.productId, manifest)
    })
  }

  /**
   * 统一获取设备的 Usage 信息
   * 抹平 WebHID (collections) 和 NodeHID (usagePage/usage) 的差异
   */
  private getDeviceUsages(device: any): { usagePage: number; usage: number }[] {
    // 1. WebHID 模式: 检查 collections 数组
    if ('collections' in device && Array.isArray(device.collections)) {
      return device.collections.map((c: any) => ({
        usagePage: c.usagePage,
        usage: c.usage,
      }))
    }

    // 2. NodeHID 模式: 检查直接属性
    // 注意：部分系统下 usagePage/usage 可能为 0，所以不能简单用 if (device.usagePage) 判断
    if (typeof device.usagePage === 'number' && typeof device.usage === 'number') {
      return [{ usagePage: device.usagePage, usage: device.usage }]
    }

    return []
  }

  /**
   * WebHID requestDevice 弹窗阶段只做“能列出来”的授权过滤。
   *
   * 授权弹窗阶段只按 VID/PID 过滤。
   *
   * 关键：WebHID requestDevice 的 usagePage/usage 匹配依赖浏览器看到的顶层 collection。
   * 复合 HID、接收器或不同系统驱动下，collection 暴露可能和 manifest 里的目标接口不完全一致。
   * 如果这里提前带 usagePage/usage，设备会直接不出现在授权框里。
   * 因此弹窗只负责“把同 VID/PID 的设备列出来”，拿到授权后再由 handleDeviceConnected 严格检查 channel。
   */
  private toWebHIDRequestFilters(filters: HidDeviceFilter[]): HidDeviceFilter[] {
    const filterMap = new Map<string, HidDeviceFilter>()

    for (const filter of filters) {
      const requestFilter: HidDeviceFilter = {
        vendorId: filter.vendorId,
        productId: filter.productId,
      }
      const key = `${requestFilter.vendorId}-${requestFilter.productId ?? ''}`
      filterMap.set(key, requestFilter)
    }

    return Array.from(filterMap.values())
  }

  /**
   * 串行处理 WebHID close/restore，避免旧 HIDDevice 尚未关闭时打开重枚举后的新对象。
   */
  private enqueueWebHidLifecycle(task: () => Promise<void>): void {
    this.webHidLifecycle = this.webHidLifecycle.then(task).catch(() => undefined)
  }

  /**
   * 热插拔事件防抖：最后一次事件结束一小段时间后，再从 navigator.hid.getDevices()
   * 读取当前完整快照。不要直接使用 connect 事件携带的早期 HIDDevice。
   */
  private scheduleWebHidRestore(): void {
    if (this.webHidRestoreTimer) {
      globalThis.clearTimeout(this.webHidRestoreTimer)
    }

    this.webHidRestoreTimer = globalThis.setTimeout(() => {
      this.webHidRestoreTimer = null
      this.enqueueWebHidLifecycle(() => this.restore())
    }, WEB_HID_HOTPLUG_SETTLE_MS)
  }

  private setupHotPlugListeners() {
    if (typeof navigator !== 'undefined' && navigator.hid) {
      navigator.hid.addEventListener('connect', () => {
        this.scheduleWebHidRestore()
      })
      navigator.hid.addEventListener('disconnect', (e) => {
        this.enqueueWebHidLifecycle(async () => {
          const target = Array.from(this.devices.entries()).find(([_, dev]) => {
            // HidChannel 暴露 nativeDevice 时，用原生对象定位当前断开的实例。
            return dev.channel.nativeDevice === e.device
          })

          if (!target) return

          const [id, dev] = target
          this.handleUsbWiredMouseDisconnected(dev)
          await dev.close()
          this.devices.delete(id)
          this.emit(ManagerEvents.DeviceDisconnected, dev)
        })
        this.scheduleWebHidRestore()
      })
    }
  }

  /** 是否为 USB 直连鼠标（非底座代理子设备）。 */
  private isUsbDirectMouse(device: BaseDevice): boolean {
    return device.manifest.type === 'mouse' && !device.parentDevice
  }

  /**
   * 有线鼠标 HID 掉线后，让仍在线的底座一次性透传探测 2.4G 子鼠标并挂到 subDevices。
   * 不做周期轮询，避免干扰固件升级。
   */
  private handleUsbWiredMouseDisconnected(device: BaseDevice): void {
    if (this.isPairingDevice(device)) return
    if (!this.isUsbDirectMouse(device)) return
    if (device.getConnectionType() !== ConnectionType.Wired) return

    this.pendingDockChildRefreshAfterWiredMouseLeave = true
    void this.refreshConnectedDockChildren().then((refreshed) => {
      if (refreshed > 0) {
        this.pendingDockChildRefreshAfterWiredMouseLeave = false
      }
    })
  }

  /** 底座在有线鼠标刚拔出后的重连窗口内连上时，延迟补一次子鼠标探测。 */
  private maybeRefreshDockChildAfterWiredMouseLeave(device: BaseDevice): void {
    if (!this.pendingDockChildRefreshAfterWiredMouseLeave) return
    if (device.manifest.type !== 'base') return
    if (device.isFirmwareUpdateMode()) return

    this.pendingDockChildRefreshAfterWiredMouseLeave = false
    const dockId = device.id
    // 一次性延迟：等 WebHID 重枚举窗口过去，避免与固件版本查询抢同一通道（不是周期轮询）
    globalThis.setTimeout(() => {
      const dock = Array.from(this.devices.values()).find((item) => item.id === dockId)
      if (!dock?.channel?.isConnected || dock.isFirmwareUpdateMode()) return
      void this.emitCurrentConnectedMouseOnDock(dock)
    }, 800)
  }

  /** 对当前所有在线、非升级态底座各调用一次 emitCurrentConnectedMouse。 */
  private async refreshConnectedDockChildren(): Promise<number> {
    let refreshed = 0
    for (const device of this.devices.values()) {
      if (device.manifest.type !== 'base') continue
      if (device.isFirmwareUpdateMode()) continue
      if (!device.channel?.isConnected) continue
      const ok = await this.emitCurrentConnectedMouseOnDock(device)
      if (ok) refreshed += 1
    }
    return refreshed
  }

  private async emitCurrentConnectedMouseOnDock(device: BaseDevice): Promise<boolean> {
    if (this.isPairingDevice(device)) return false
    const dock = device as BaseDevice & {
      emitCurrentConnectedMouse?: () => Promise<void>
    }
    if (typeof dock.emitCurrentConnectedMouse !== 'function') return false
    try {
      await dock.emitCurrentConnectedMouse()
      return true
    } catch (error) {
      console.warn(
        `[RapooDeviceManager] emitCurrentConnectedMouse failed (${device.manifest.model}):`,
        error,
      )
      return false
    }
  }

  /**
   * 用户在 Boot 多型号消歧弹窗中选定型号后调用。
   * 会写入归属（内存 + sessionStorage）并重试挂起的 Boot 建连。
   */
  public async claimBootOwner(
    vendorId: number,
    productId: number,
    model: string,
  ): Promise<boolean> {
    const key = this.bootIdentityKey(vendorId, productId)
    const candidates = this.listBootCandidateModels(vendorId, productId)
    if (!candidates.includes(model)) {
      console.warn(
        `[RapooDeviceManager] claimBootOwner: model "${model}" 不在候选 ${candidates.join(',')}`,
      )
      return false
    }
    this.setBootOwner(key, model)

    const rawDevice = this.pendingAmbiguousBoots.get(key)
    this.pendingAmbiguousBoots.delete(key)
    if (rawDevice) {
      await this.handleDeviceConnected(rawDevice)
    }
    return true
  }

  private resolveManifestForDevice(
    vid: number,
    pid: number,
  ):
    | { kind: 'ok'; manifest: NormalizedDeviceManifest }
    | { kind: 'none' }
    | { kind: 'ambiguous'; models: string[] } {
    const manifests = JSON.parse(JSON.stringify(this.manifests)) as NormalizedDeviceManifest[]

    const byApp = manifests.find((m) => {
      const pids = Array.isArray(m.hardware.productId)
        ? m.hardware.productId
        : [m.hardware.productId]
      return m.hardware.vendorId === vid && pids.includes(pid)
    })
    if (byApp) return { kind: 'ok', manifest: byApp }

    const bootMatches = manifests.filter((m) =>
      m.channels.some((channel) => {
        if (channel.usageType !== 'update') return false
        if (channel.vendorId === undefined || channel.productId === undefined) return false
        const pids = Array.isArray(channel.productId) ? channel.productId : [channel.productId]
        return channel.vendorId === vid && pids.includes(pid)
      }),
    )
    if (bootMatches.length === 0) return { kind: 'none' }
    if (bootMatches.length === 1) return { kind: 'ok', manifest: bootMatches[0] }

    const preferredModel =
      this.bootOwnerModelByKey.get(this.bootIdentityKey(vid, pid)) ??
      this.loadBootOwnerFromStorage(this.bootIdentityKey(vid, pid))
    if (preferredModel) {
      const preferred = bootMatches.find((m) => m.model === preferredModel)
      if (preferred) {
        // 灌回内存，后续同会话一致
        this.bootOwnerModelByKey.set(this.bootIdentityKey(vid, pid), preferredModel)
        return { kind: 'ok', manifest: preferred }
      }
    }

    for (const m of bootMatches) {
      const onlineApp = Array.from(this.devices.values()).find(
        (d) => d.manifest.model === m.model && !d.isFirmwareUpdateMode(),
      )
      if (onlineApp) return { kind: 'ok', manifest: m }
    }

    // 刷新后无 APP 前置记录：禁止静默选第一个（会刷错固件包），交 Web 消歧
    return { kind: 'ambiguous', models: bootMatches.map((m) => m.model) }
  }

  private listBootCandidateModels(vid: number, pid: number): string[] {
    return this.manifests
      .filter((m) =>
        m.channels.some((channel) => {
          if (channel.usageType !== 'update') return false
          if (channel.vendorId === undefined || channel.productId === undefined) return false
          const pids = Array.isArray(channel.productId) ? channel.productId : [channel.productId]
          return channel.vendorId === vid && pids.includes(pid)
        }),
      )
      .map((m) => m.model)
  }

  private bootIdentityKey(vendorId: number, productId: number): string {
    return `${vendorId}:${productId}`
  }

  private bootOwnerStorageKey(identityKey: string): string {
    return `rapoo.device-sdk.bootOwner:${identityKey}`
  }

  private setBootOwner(identityKey: string, model: string): void {
    this.bootOwnerModelByKey.set(identityKey, model)
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(this.bootOwnerStorageKey(identityKey), model)
      }
    } catch {
      // 隐私模式 / 非浏览器：仅内存
    }
  }

  private loadBootOwnerFromStorage(identityKey: string): string | undefined {
    try {
      if (typeof sessionStorage === 'undefined') return undefined
      return sessionStorage.getItem(this.bootOwnerStorageKey(identityKey)) ?? undefined
    } catch {
      return undefined
    }
  }

  /** APP 在线时登记其 Boot 身份归属，供随后 Upgrader 消歧（含刷新后） */
  private rememberBootOwnerFromAppManifest(manifest: NormalizedDeviceManifest): void {
    for (const channel of manifest.channels) {
      if (channel.usageType !== 'update') continue
      if (channel.vendorId === undefined || channel.productId === undefined) continue
      const pids = Array.isArray(channel.productId) ? channel.productId : [channel.productId]
      for (const productId of pids) {
        this.setBootOwner(this.bootIdentityKey(channel.vendorId, productId), manifest.model)
      }
    }
  }
}
